// Line-aware Type Writer clip. The old implementation clipped the whole
// element with a single `inset(0 X% 0 0)`, so multi-line text revealed all
// lines at once. Here we measure the rendered text line boxes and build a
// polygon that reveals lines sequentially (each line left→right), with time
// distributed proportionally to line width so typing speed stays constant.

const measureCache = new WeakMap();

const clamp01 = (v) => Math.max(0, Math.min(1, v));

function collectLines(el) {
  const containerRect = el.getBoundingClientRect();
  if (!containerRect.width || !containerRect.height) return null;

  const walker = document.createTreeWalker(
    el.querySelector(".boxStyle") || el,
    NodeFilter.SHOW_TEXT
  );
  const range = document.createRange();
  const rects = [];
  let node;
  while ((node = walker.nextNode())) {
    if (!node.nodeValue || !node.nodeValue.trim()) continue;
    range.selectNodeContents(node);
    const list = range.getClientRects();
    for (let i = 0; i < list.length; i++) {
      const r = list[i];
      if (r.width > 0 && r.height > 0) rects.push(r);
    }
  }
  if (!rects.length) return null;

  rects.sort((a, b) => a.top - b.top || a.left - b.left);
  const lines = [];
  for (const r of rects) {
    const last = lines[lines.length - 1];
    const center = r.top + r.height / 2;
    if (last && center >= last.top && center <= last.bottom) {
      last.top = Math.min(last.top, r.top);
      last.bottom = Math.max(last.bottom, r.bottom);
      last.left = Math.min(last.left, r.left);
      last.right = Math.max(last.right, r.right);
    } else {
      lines.push({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
    }
  }

  // Expand each band to the midpoint between neighbours (and to the container
  // edges at the extremes) so ascenders/descenders never get sliced.
  const { top: cTop, left: cLeft, width: W, height: H } = containerRect;
  return lines.map((ln, i) => {
    const bandTop = i === 0 ? cTop : (lines[i - 1].bottom + ln.top) / 2;
    const bandBottom = i === lines.length - 1 ? cTop + H : (ln.bottom + lines[i + 1].top) / 2;
    return {
      top: clamp01((bandTop - cTop) / H),
      bottom: clamp01((bandBottom - cTop) / H),
      left: clamp01((ln.left - cLeft) / W),
      right: clamp01((ln.right - cLeft) / W),
      width: Math.max(0.0001, (ln.right - ln.left) / W),
    };
  });
}

function getLines(el) {
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  const len = (el.textContent || "").length;
  const cached = measureCache.get(el);
  if (cached && cached.w === w && cached.h === h && cached.len === len) return cached.lines;
  const lines = collectLines(el);
  measureCache.set(el, { w, h, len, lines });
  return lines;
}

// reveal ∈ [0,1] → clip-path string. Falls back to the single-inset behaviour
// when the content has fewer than two text lines (or nothing measurable).
export function getTypewriterClipPath(el, reveal) {
  const r = clamp01(reveal);
  if (r <= 0) return "inset(0 100% 0 0)";
  if (r >= 1) return "inset(0 0% 0 0)";

  const lines = el ? getLines(el) : null;
  if (!lines || lines.length < 2) {
    return `inset(0 ${(1 - r) * 100}% 0 0)`;
  }

  const totalWidth = lines.reduce((sum, ln) => sum + ln.width, 0);
  let remaining = r * totalWidth;
  let i = 0;
  while (i < lines.length - 1 && remaining > lines[i].width) {
    remaining -= lines[i].width;
    i++;
  }
  const ln = lines[i];
  const f = Math.min(1, remaining / ln.width);
  const x = ((ln.left + f * (ln.right - ln.left)) * 100).toFixed(3);
  const yTop = (ln.top * 100).toFixed(3);
  const yBottom = (ln.bottom * 100).toFixed(3);
  return `polygon(0% 0%, 100% 0%, 100% ${yTop}%, ${x}% ${yTop}%, ${x}% ${yBottom}%, 0% ${yBottom}%)`;
}
